import { MediasPublicConstants, MediasPublicModels } from '@features/products/medias';
import { MediaClazzType, MediaLocation } from '@features/products/medias/domain/models/medias.models';
import { ProductPublicFunctions, ProductPublicModels } from '@features/products/product';

// #region Paso-1 y Paso-2
export function fetchProductImages(
    color: ProductPublicModels.PublicProductColor,
    location: MediaLocation,
    imageType: MediaClazzType,
    imageTypeAlternative: MediaClazzType,
    set?: number
): MediasPublicModels.PublicMediaResolved[] {
    let productImages = ProductPublicFunctions.GetImagesForColor(color, location, {
        clazzType: imageType,
        set,
    }) ?? [];

    if (!productImages.length && imageTypeAlternative) {
        productImages = ProductPublicFunctions.GetImagesForColor(color, location, { clazzType: imageTypeAlternative }) ?? [];
    }

    return productImages;
}
// #endregion

// #region Paso-3
export function filterFallbackImages(
    productImages: MediasPublicModels.PublicMediaResolved[],
    discardFallback: boolean
): MediasPublicModels.PublicMediaResolved[] {
    if (discardFallback) {
        return productImages.filter(image => !image.isFallback);
    }
    return productImages;
}
// #endregion

// #region Paso-4
export function fetchDefaultImageType(
    color: ProductPublicModels.PublicProductColor,
    locationDefault: MediaLocation,
    defaultImageType: string
): MediasPublicModels.PublicMediaResolved {
    return ProductPublicFunctions.GetImagesForColor(color, locationDefault)
        .find((img) => img.path.includes(defaultImageType) || img.idMedia === defaultImageType);
}
// #endregion

// #region Paso-5
export function selectFirstImage(
    productImages: MediasPublicModels.PublicMediaResolved[],
    discardFallback: boolean
): MediasPublicModels.PublicMediaResolved {
    if (discardFallback && productImages.length > 1) {
        return productImages.filter(img => !img.isFallback)[0];
    }
    return productImages[0];
}
// #endregion

// #region Paso-6
export function fetchSecondaryImage(
    color: ProductPublicModels.PublicProductColor,
    locationDefault: MediaLocation
): MediasPublicModels.PublicMediaResolved {
    const imagesSecondaries = ProductPublicFunctions.GetImagesForColor(color, locationDefault, { clazzType: 'secondary' });
    return imagesSecondaries[0];
}
// #endregion

// #region Paso-7
export function fetchAnyImage(
    color: ProductPublicModels.PublicProductColor,
    locationDefault: MediaLocation
): MediasPublicModels.PublicMediaResolved {
    const images = ProductPublicFunctions.GetImagesForColor(color, locationDefault, { clazzType: undefined });
    return images[0];
}
// #endregion

export function getImageForProductViewUseCase({
    color,
    productImagesLocation,
    locationDefault,
    imageType,
    imageTypeAlternative,
    defaultImageType,
    options,
}): MediasPublicModels.PublicMediaResolved {
    // Paso 1 y Paso 2: Obtener imágenes usando `imageType` o `imageTypeAlternative`
    let productImages = fetchProductImages(color, productImagesLocation, imageType, imageTypeAlternative, options?.set);
    // Paso 3: filtramos imágenes para quitar las de tipo `fallback` si `options.discardFallback` está activado
    productImages = filterFallbackImages(productImages, options.discardFallback);

    let image: MediasPublicModels.PublicMediaResolved = productImages[0];
    // Paso 4: obtenemos la imagen default usando `defaultImageType` en `locationDefault`
    if (defaultImageType) {
        const defaultImage = fetchDefaultImageType(color, locationDefault, defaultImageType);
        image = defaultImage ?? image;
    }
    // Paso 5: cogemos la primera imagen disponible
    if (!image) {
        image = selectFirstImage(productImages, options.discardFallback);
    }
    // Paso 6: obtenemos imagen secundaria en `locationDefault`
    if (!image && options.secondaryImage) {
        image = fetchSecondaryImage(color, locationDefault);
    }
    // Paso 7: buscar culaquier imagen si no hay ninguna
    if (!image) {
        image = fetchAnyImage(color, locationDefault);
    }

    return image;
}

export function getCarouselImagesUseCase({
    color,
    location,
    imageType,
    image,
}): MediasPublicModels.PublicMediaResolved[] {
    let productImages = ProductPublicFunctions.GetImagesForColor(
        color,
        MediasPublicConstants.PublicMediaLocationTypeValues[1],
        { clazzType: location === MediaLocation.search ? null : imageType }
    ) ?? [];

    // Ordenar imágenes y filtrar `isFallback`
    if (productImages.some(media => media.path === image?.path)) {
        productImages = productImages
            .filter(img => !img.isFallback)
            .sort((a, b) => (b.path === image?.path ? 1 : 0) - (a.path === image?.path ? 1 : 0));
    } else if (image) {
        productImages.unshift(image);
    }

    return productImages;
}

export function getMultipleMediasUseCase({
    color,
    mediaType,
    totalMedias,
}): MediasPublicModels.PublicMediaResolved[] {
    const productMedias = ProductPublicFunctions.GetImagesForColor(
        color,
        MediasPublicConstants.PublicMediaLocationTypeValues[1],
        { clazzType: null }
    ).filter(media => !media.isFallback && media.contentType.type === mediaType) ?? [];

    const productMediasSorted = Array(totalMedias).fill(undefined);
    for (const item of productMedias) {
        productMediasSorted[item.position] = item;
    }

    return productMediasSorted.slice(0, totalMedias);
}
